import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Users, AlertCircle, DollarSign, Clock, ArrowRight, Loader2, RefreshCw, UserCheck, Ticket, Calendar, ShoppingBag, FileText, Bell, Trophy, MapPin, CreditCard, Repeat, Cake, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, formatCents, type DashboardStats } from '@/lib/api';

function relativeTime(iso: string): string {
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const firstName = user?.firstName ?? user?.username ?? '';

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await apiCall<DashboardStats>('/v1/stats', { bearerToken: token });
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  const overdueOwed = stats?.overdue.total_owed_cents ?? 0;
  const monthLabel = stats?.month
    ? new Date(`${stats.month}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'this month';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Overview</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back{firstName ? `, ${firstName}` : ''}. Snapshot for {monthLabel}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button asChild size="sm">
            <Link to="/dashboard/members">
              Manage members <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {error && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* Stat cards — row 1: revenue / overdue */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Users className="h-4 w-4" />}
          label="Active members"
          value={stats?.members.active ?? '–'}
          hint={stats ? `${stats.members.suspended} suspended, ${stats.members.inactive} inactive` : 'Loading…'}
        />
        <StatCard
          icon={<AlertCircle className="h-4 w-4 text-destructive" />}
          label="Overdue"
          value={stats?.overdue.count ?? '–'}
          hint={stats ? `${formatCents(overdueOwed)} owed for ${monthLabel}` : 'Loading…'}
          {...((stats?.overdue.count ?? 0) > 0 ? { accent: 'destructive' as const } : {})}
        />
        <StatCard
          icon={<DollarSign className="h-4 w-4 text-green-600" />}
          label={`Revenue ${monthLabel.split(' ')[0]}`}
          value={stats ? formatCents(stats.payments_this_month.total_cents) : '–'}
          hint={stats ? `${stats.payments_this_month.count} payment${stats.payments_this_month.count === 1 ? '' : 's'}` : 'Loading…'}
        />
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label="Today"
          value={stats ? formatCents(stats.payments_today.total_cents) : '–'}
          hint={stats ? `${stats.payments_today.count} payment${stats.payments_today.count === 1 ? '' : 's'} today` : 'Loading…'}
        />
      </div>

      {/* Stat cards — row 2: activity */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<UserCheck className="h-4 w-4" />}
          label="Check-ins today"
          value={stats?.checkins_today.count ?? '–'}
          hint="Public /checkin + dashboard"
        />
        <StatCard
          icon={<Ticket className="h-4 w-4" />}
          label="Day passes today"
          value={stats?.day_passes_today.count ?? '–'}
          hint={stats ? formatCents(stats.day_passes_today.total_cents) + ' collected' : 'Loading…'}
        />
        <StatCard
          icon={<ShoppingBag className="h-4 w-4" />}
          label="Store sales today"
          value={stats?.sales_today.count ?? '–'}
          hint={stats ? formatCents(stats.sales_today.total_cents) + ' collected' : 'Loading…'}
        />
        <StatCard
          icon={<FileText className="h-4 w-4" />}
          label="No waiver"
          value={stats?.unsigned_waivers.count ?? '–'}
          hint="Active members without a signed waiver"
          {...((stats?.unsigned_waivers.count ?? 0) > 0 ? { accent: 'warn' as const } : {})}
        />
      </div>

      {/* Recurring revenue (only if any active subs) */}
      {stats && stats.mrr && stats.mrr.active_count > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Repeat className="h-4 w-4" /> Recurring revenue</CardTitle>
              <CardDescription>{stats.mrr.active_count} active subscription{stats.mrr.active_count === 1 ? '' : 's'} · {formatCents(stats.mrr.monthly_cents)} / month</CardDescription>
            </div>
            <div className="text-right">
              <div className="text-3xl font-semibold tabular-nums">{formatCents(stats.mrr.monthly_cents)}</div>
              <div className="text-xs text-muted-foreground">MRR · {formatCents(stats.mrr.monthly_cents * 12)} ARR</div>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {stats.mrr.by_plan.map((p) => (
                <li key={p.plan} className="py-2 text-sm flex items-center justify-between gap-3">
                  <span className="font-medium truncate flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    {p.plan}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {p.n} × · <span className="font-medium text-foreground">{formatCents(p.cents)}/mo</span>
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Two-up: upcoming sessions + low stock */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-4 w-4" /> Upcoming sessions</CardTitle>
              <CardDescription>Next 7 days</CardDescription>
            </div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/schedule">View all <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
          </CardHeader>
          <CardContent>
            {!stats ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> :
              stats.upcoming_sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No sessions scheduled. <Link to="/dashboard/schedule" className="underline">Schedule one</Link>.
                </p>
              ) : (
                <ul className="divide-y">
                  {stats.upcoming_sessions.map((s) => (
                    <li key={s.id} className="py-2 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.class_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(s.starts_at.replace(' ', 'T')).toLocaleString()}
                          {s.coach_name && ` · ${s.coach_name}`}
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0">{s.registered_count}/{s.capacity}</Badge>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Low stock</CardTitle>
              <CardDescription>3 or fewer units left</CardDescription>
            </div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/store">Manage store <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
          </CardHeader>
          <CardContent>
            {!stats ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> :
              stats.low_stock.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">All inventory healthy.</p>
              ) : (
                <ul className="divide-y">
                  {stats.low_stock.map((p) => (
                    <li key={p.id} className="py-2 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0"><div className="font-medium truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground tabular-nums">{formatCents(p.price_cents)}</div>
                      </div>
                      <Badge variant={p.stock_qty === 0 ? 'destructive' : 'outline'} className="shrink-0">
                        {p.stock_qty} left
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Birthdays + USAW expiring (compact two-up) */}
      {stats && (stats.birthdays_this_week.length > 0 || stats.usaw_expiring.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {stats.birthdays_this_week.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Cake className="h-4 w-4" /> Birthdays this week</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {stats.birthdays_this_week.map((b) => (
                    <li key={b.id} className="py-1.5 text-sm flex items-center justify-between">
                      <Link to={`/dashboard/members/${b.id}`} className="hover:underline">{b.name}</Link>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {new Date(b.date_of_birth + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {stats.usaw_expiring.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-900/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> USAW expiring</CardTitle>
                <CardDescription>Within 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {stats.usaw_expiring.map((u) => (
                    <li key={u.id} className="py-1.5 text-sm flex items-center justify-between">
                      <Link to={`/dashboard/members/${u.id}`} className="hover:underline">{u.name}</Link>
                      <span className={'text-xs tabular-nums ' + (u.days_left <= 0 ? 'text-destructive font-medium' : u.days_left <= 7 ? 'text-amber-700' : 'text-muted-foreground')}>
                        {u.days_left <= 0 ? 'expired' : `${u.days_left}d`}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Upcoming events (only shown if any exist) */}
      {stats && stats.upcoming_events && stats.upcoming_events.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Trophy className="h-4 w-4" /> Upcoming tournaments</CardTitle>
              <CardDescription>Next 5 events</CardDescription>
            </div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/events">View all <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {stats.upcoming_events.map((e) => {
                const start = new Date(e.starts_at);
                const days = Math.round((start.getTime() - Date.now()) / 86_400_000);
                return (
                  <li key={e.id} className="py-2 text-sm flex items-center justify-between gap-3">
                    <Link to={`/dashboard/events/${e.id}`} className="min-w-0 flex-1 hover:underline">
                      <div className="font-medium truncate">{e.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span>{start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}{days >= 0 && days <= 14 ? ` · in ${days}d` : ''}</span>
                        {e.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location_name}</span>}
                        {e.sanctioning_body && <Badge variant="outline" className="text-xs">{e.sanctioning_body}</Badge>}
                      </div>
                    </Link>
                    <Badge variant="outline" className="shrink-0">{e.registered_count} registered</Badge>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Notifications health (only if anything skipped/failed in 7d) */}
      {stats && stats.notification_status_7d.some((s) => s.status !== 'sent') && (
        <Card className="border-amber-200 dark:border-amber-900/50">
          <CardContent className="py-3 text-xs flex items-center gap-2 text-amber-900 dark:text-amber-200">
            <Bell className="h-4 w-4 shrink-0" />
            <div className="flex-1">
              In the last 7 days:&nbsp;
              {stats.notification_status_7d.map((s, i) => (
                <span key={s.status}>
                  {i > 0 && ', '}
                  <strong>{s.n}</strong> {s.status}
                </span>
              ))}.{' '}
              <Link to="/dashboard/notifications" className="underline">View log</Link>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent payments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent payments</CardTitle>
          <CardDescription>The 10 most recent cash payments recorded.</CardDescription>
        </CardHeader>
        <CardContent>
          {!stats ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : stats.recent_payments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No payments yet. Head to <Link to="/dashboard/members" className="underline">Members</Link> to add the first one.
            </p>
          ) : (
            <ul className="divide-y">
              {stats.recent_payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {p.member_id ? (
                        <Link to={`/dashboard/members/${p.member_id}`} className="hover:underline">{p.member_name ?? '(unknown)'}</Link>
                      ) : (p.member_name ?? '(member deleted)')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {relativeTime(p.paid_at)}
                      {p.covers_month && ` · covers ${p.covers_month}`}
                      <Badge variant="outline" className="ml-2">{p.method}</Badge>
                    </div>
                  </div>
                  <div className="text-sm font-medium tabular-nums">{formatCents(p.amount_cents)}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard(props: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint: string;
  accent?: 'destructive' | 'warn';
}) {
  const accentClass = props.accent === 'destructive' ? 'text-destructive' : props.accent === 'warn' ? 'text-amber-600' : '';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2">
          {props.icon}
          {props.label}
        </CardDescription>
        <CardTitle className={'text-3xl ' + accentClass}>
          {props.value}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">{props.hint}</CardContent>
    </Card>
  );
}
